/**
 * Assessment question + option translations.
 *
 * Backend `/api/questions` always returns ENGLISH. We translate client-side
 * keyed by question.id so the backend stays language-agnostic.
 *
 * Hindi (hi) and Marathi (mr) are written conversationally — the way you'd
 * actually phrase the question to a friend — NOT a literal translation.
 *
 * Each entry shape:
 *   <question_id>: {
 *     hi: { q: '<question text>', o: ['1', '2', '3', '4', '5'] },
 *     mr: { q: '...',             o: [...] },
 *   }
 * Option arrays are indexed by value-1 (value 1 → o[0], …, value 5 → o[4]).
 */

const QUESTION_I18N = {
  // ===== PHYSICAL =====
  physical_1: {
    hi: {
      q: 'हफ़्ते में कितनी बार आप शरीरिक एक्टिविटी करते हैं?',
      o: ['कभी नहीं या लगभग कभी नहीं', 'हफ़्ते में एक बार', 'हफ़्ते में 2-3 बार', 'हफ़्ते में 4-5 बार', 'रोज़ या लगभग रोज़'],
    },
    mr: {
      q: 'आठवड्यातून किती वेळा तुम्ही शारीरिक व्यायाम करता?',
      o: ['कधीच किंवा फारच क्वचित', 'आठवड्यातून एकदा', 'आठवड्यातून 2-3 वेळा', 'आठवड्यातून 4-5 वेळा', 'रोज किंवा जवळजवळ रोज'],
    },
  },
  physical_2: {
    hi: {
      q: 'अपनी रोज़ की ऊर्जा, नींद और खान-पान को कुल मिलाकर कैसे रेट करेंगे?',
      o: ['बहुत ख़राब — थका, बेचैन, अनियमित', 'ख़राब — अक्सर थकान, कमज़ोर आदतें', 'ठीक-ठाक — दिन-ब-दिन बदलता है', 'अच्छा — ज़्यादातर एनर्जेटिक और संतुलित', 'बहुत बढ़िया — अनुशासित और भरपूर ऊर्जा'],
    },
    mr: {
      q: 'तुमची रोजची ऊर्जा, झोप आणि आहार एकत्रित कशी रेट कराल?',
      o: ['खूप वाईट — थकवा, अस्थिर सवयी', 'वाईट — वारंवार थकवा, कमजोर सवयी', 'ठीकठाक — दिवसागणिक बदलतं', 'चांगलं — बहुतांश एनर्जेटिक आणि संतुलित', 'उत्तम — शिस्तबद्ध, उच्च ऊर्जा'],
    },
  },
  physical_3: {
    hi: {
      q: 'आपकी नींद और खान-पान कैसा है?',
      o: ['बहुत ख़राब — दोनों अनियमित', 'ख़राब — सुधार चाहिए', 'ठीक-ठाक — कभी अच्छा कभी ख़राब', 'अच्छा — ज़्यादातर सेहतमंद और आराम भरा', 'बहुत बढ़िया — अनुशासित और ताज़गी देने वाला'],
    },
    mr: {
      q: 'तुमची झोप आणि खाण्या-पिण्याची सवय कशी आहे?',
      o: ['खूप वाईट — दोन्ही अनियमित', 'वाईट — खूप सुधारणा हवी', 'ठीक — कधी चांगलं, कधी वाईट', 'चांगली — बहुतेक वेळा निरोगी', 'उत्तम — शिस्तबद्ध आणि आरामदायी'],
    },
  },

  // ===== MENTAL =====
  mental_1: {
    hi: {
      q: 'कितनी बार आप तनाव या मानसिक रूप से बोझिल महसूस करते हैं?',
      o: ['लगभग रोज़', 'अक्सर', 'कभी-कभी', 'कम ही', 'लगभग कभी नहीं'],
    },
    mr: {
      q: 'किती वेळा तुम्हाला ताण किंवा मानसिक ओझं जाणवतं?',
      o: ['जवळजवळ रोज', 'बऱ्याचदा', 'कधीकधी', 'क्वचितच', 'जवळजवळ कधीच नाही'],
    },
  },
  mental_2: {
    hi: {
      q: 'अपने ध्यान और विचारों पर कितना कंट्रोल रख पाते हैं?',
      o: ['बहुत कम — हमेशा भटका रहता हूँ', 'कम — ध्यान बार-बार टूटता है', 'ठीक-ठाक — कभी कभी फोकस', 'अच्छा — ज़रूरत पड़े तो फ़ोकस कर लेता हूँ', 'बहुत बढ़िया — तेज़ और सटीक सोच'],
    },
    mr: {
      q: 'तुमच्या लक्षावर आणि विचारांवर तुमचा किती ताबा आहे?',
      o: ['खूप कमी — सतत भरकटतो', 'कमी — लक्ष टिकत नाही', 'ठीक — कधीकधी फोकस होतो', 'चांगला — गरजेला फोकस करता येतो', 'उत्तम — तीक्ष्ण आणि नियोजित'],
    },
  },

  // ===== EMOTIONAL =====
  emotional_1: {
    hi: {
      q: 'अपने रिश्तों में आप कितने भावनात्मक रूप से स्थिर और सपोर्टेड महसूस करते हैं?',
      o: ['बहुत अस्थिर और असुरक्षित', 'अक्सर चिंतित; झगड़े थका देते हैं', 'मैनेज होता है पर बदलता रहता है', 'ज़्यादातर शांत; अच्छे से संभाल लेता हूँ', 'गहराई से संतुलित; समझदारी से सुलझाता हूँ'],
    },
    mr: {
      q: 'तुमच्या नात्यांमध्ये तुम्हाला किती भावनिक स्थिरता आणि आधार वाटतो?',
      o: ['खूप अस्थिर आणि असुरक्षित', 'बऱ्याचदा अस्वस्थ; भांडणं दमवतात', 'सांभाळतो पण सातत्य नाही', 'बहुतांश शांत; चांगलं सांभाळतो', 'पूर्ण संतुलित; समजुतीनं सोडवतो'],
    },
  },
  emotional_2: {
    hi: {
      q: 'रिश्तों और झगड़ों को कैसे संभालते हैं?',
      o: ['बहुत मुश्किल से — झगड़े थका देते हैं', 'अक्सर बच निकलता हूँ, बात नहीं करता', 'ठीक-ठाक — ज़्यादातर हालात संभाल लेता हूँ', 'अच्छे से — शांति से बात करता हूँ', 'बहुत बढ़िया — समझदारी से सुलझाता हूँ'],
    },
    mr: {
      q: 'नाती आणि भांडणं तुम्ही कशी हाताळता?',
      o: ['खूप कठीण — भांडणं दमवतात', 'टाळतो, बोलत नाही', 'ठीक — बहुतेक वेळा सांभाळतो', 'चांगलं — शांतपणे बोलतो', 'उत्तम — समजुतीनं सोडवतो'],
    },
  },

  // ===== FINANCIAL =====
  financial_1: {
    hi: {
      q: 'आपकी आर्थिक स्थिति कितनी स्थिर है और पैसों को लेकर कितना तनाव रहता है?',
      o: ['बहुत अस्थिर — रोज़ बोझ लगता है', 'महीना मुश्किल से चलता है — अक्सर चिंता', 'स्थिर पर सीमित — कभी-कभी चिंता', 'आरामदायक और अंदाज़े के मुताबिक़ — कम चिंता', 'बहुत स्थिर, बचत भी है — कोई तनाव नहीं'],
    },
    mr: {
      q: 'तुमची आर्थिक स्थिती किती स्थिर आहे आणि पैशांचा तुम्हाला किती ताण येतो?',
      o: ['खूप अस्थिर — रोज ओझं वाटतं', 'महिना कसाबसा भागतो — बऱ्याचदा काळजी', 'स्थिर पण मर्यादित — कधीकधी काळजी', 'आरामदायी आणि अंदाजे — क्वचितच काळजी', 'खूप स्थिर, बचत आहे — कोणताच ताण नाही'],
    },
  },
  financial_2: {
    hi: {
      q: 'क्या आपके पास बचत और फ़ाइनेंशियल प्लानिंग है?',
      o: ['कोई बचत नहीं, कोई प्लान नहीं', 'थोड़ी बचत, कोई असली प्लान नहीं', 'कुछ बचत, बेसिक प्लान', 'अच्छी बचत और साफ़ प्लान', 'मज़बूत बचत, निवेश और लक्ष्य'],
    },
    mr: {
      q: 'तुमची बचत आणि आर्थिक नियोजन कसं आहे?',
      o: ['बचत नाही, नियोजन नाही', 'थोडी बचत, ठोस नियोजन नाही', 'काही बचत, बेसिक नियोजन', 'चांगली बचत आणि स्पष्ट नियोजन', 'भक्कम बचत, गुंतवणूक आणि उद्दिष्टं'],
    },
  },
  financial_3: {
    hi: {
      q: 'पैसों को लेकर कितना तनाव रहता है?',
      o: ['लगातार — रोज़ बोझ लगता है', 'अक्सर चिंता', 'कभी-कभी', 'कम ही पैसों की चिंता', 'लगभग कोई आर्थिक तनाव नहीं'],
    },
    mr: {
      q: 'पैशांचा तुम्हाला किती ताण येतो?',
      o: ['सतत — रोज ओझं वाटतं', 'बऱ्याचदा काळजी', 'कधीकधी', 'क्वचितच काळजी', 'जवळजवळ कोणताच आर्थिक ताण नाही'],
    },
  },

  // ===== CAREER =====
  career_1: {
    hi: {
      q: 'अपने करियर की तरक्की से कितने संतुष्ट हैं?',
      o: ['बहुत असंतुष्ट — फँसा हुआ लगता हूँ', 'धीमी तरक्की, परेशान हूँ', 'औसत तरक्की', 'अच्छी तरक्की, रास्ता सही है', 'बहुत बढ़िया — तेज़ी से आगे बढ़ रहा हूँ'],
    },
    mr: {
      q: 'तुमच्या करियरच्या प्रगतीने तुम्ही किती समाधानी आहात?',
      o: ['खूप असमाधानी — अडकल्यासारखं वाटतं', 'मंद प्रगती, चिडचिड होते', 'सरासरी प्रगती', 'चांगली प्रगती, मार्ग योग्य', 'उत्तम — झपाट्याने पुढे जातोय'],
    },
  },
  career_2: {
    hi: {
      q: 'क्या आपको अपने करियर की दिशा साफ़ दिखती है?',
      o: ['बिल्कुल नहीं — दिशा भटकी हुई', 'ज़्यादातर साफ़ नहीं', 'थोड़ी-बहुत साफ़', 'साफ़, तय रास्ता है', 'पूरी तरह जुड़ा हुआ और मक़सद वाला'],
    },
    mr: {
      q: 'तुमच्या करियरची दिशा तुम्हाला स्पष्ट आहे का?',
      o: ['अजिबात नाही — गोंधळ आहे', 'फारशी स्पष्ट नाही', 'थोडीफार स्पष्ट', 'स्पष्ट, ठरलेला मार्ग आहे', 'पूर्ण स्पष्ट आणि उद्देशपूर्ण'],
    },
  },

  // ===== LIFESTYLE =====
  lifestyle_1: {
    hi: {
      q: 'आपकी रोज़ की दिनचर्या कितनी अनुशासित और इरादतन है — आदतें, स्क्रीन टाइम और लक्ष्यों से तालमेल?',
      o: ['बहुत अनियमित — बिखरा हुआ और आसानी से भटक जाता हूँ', 'कमज़ोर ताबा; दिनचर्या लक्ष्यों के साथ नहीं चलती', 'थोड़ी रूटीन; आंशिक रूप से तालमेल', 'ज़्यादातर अनुशासित और लक्ष्यों से जुड़ी', 'पूरी तरह अनुशासित, इरादतन और जुड़ी हुई'],
    },
    mr: {
      q: 'तुमची रोजची दिनचर्या किती शिस्तबद्ध आणि हेतुपूर्ण आहे — सवयी, स्क्रीन टाइम आणि ध्येयांशी जुळवणी?',
      o: ['खूप अनियमित — गोंधळलेलं आणि सहज भरकटतं', 'कमी ताबा; दिनचर्या ध्येयांना साथ देत नाही', 'थोडंसं रूटीन; अर्धवट जुळवणी', 'बहुतांश शिस्तबद्ध आणि ध्येयांशी जुळलेलं', 'पूर्ण शिस्तबद्ध, हेतुपूर्ण आणि जुळलेलं'],
    },
  },
  lifestyle_2: {
    hi: {
      q: 'जिस जगह आप सबसे ज़्यादा वक़्त बिताते हैं (घर/ऑफ़िस), उसका आपकी सेहत पर कैसा असर है?',
      o: ['बहुत बुरा असर', 'ज़्यादातर बुरा असर', 'न अच्छा न बुरा', 'ज़्यादातर अच्छा असर', 'बहुत अच्छा — मन को उठाने वाला माहौल'],
    },
    mr: {
      q: 'तुम्ही जिथे सर्वाधिक वेळ घालवता (घर/ऑफिस), त्याचा तुमच्या आरोग्यावर कसा परिणाम होतो?',
      o: ['खूप वाईट परिणाम', 'बहुतांश वाईट परिणाम', 'न चांगला न वाईट', 'बहुतांश चांगला परिणाम', 'खूप चांगला — मन उत्साही करणारं वातावरण'],
    },
  },
  lifestyle_3: {
    hi: {
      q: 'आपकी लाइफ़स्टाइल आपके लंबे लक्ष्यों से कितनी मेल खाती है?',
      o: ['बिल्कुल मेल नहीं', 'थोड़ी-बहुत', 'कुछ हद तक', 'ज़्यादातर मेल खाती है', 'पूरी तरह तय और जुड़ी हुई'],
    },
    mr: {
      q: 'तुमची जीवनशैली तुमच्या दीर्घकालीन ध्येयांशी किती जुळते?',
      o: ['अजिबात जुळत नाही', 'थोडीफार', 'काहीशी', 'बहुतांश जुळते', 'पूर्ण जुळते आणि नियोजित'],
    },
  },
  lifestyle_4: {
    hi: {
      q: 'जिस जगह आप सबसे ज़्यादा वक़्त बिताते हैं (घर/ऑफ़िस/और कहीं), उसका आपकी सेहत पर कैसा असर है?',
      o: ['बहुत बुरा असर', 'ज़्यादातर बुरा असर', 'न अच्छा न बुरा', 'ज़्यादातर अच्छा असर', 'बहुत अच्छा — मन को उठाने वाला माहौल'],
    },
    mr: {
      q: 'तुम्ही जिथे सर्वाधिक वेळ घालवता (घर/ऑफिस/इतर), त्याचा तुमच्या आरोग्यावर कसा परिणाम होतो?',
      o: ['खूप वाईट परिणाम', 'बहुतांश वाईट परिणाम', 'न चांगला न वाईट', 'बहुतांश चांगला परिणाम', 'खूप चांगला — मन उत्साही करणारं वातावरण'],
    },
  },

  // ===== SPIRITUAL =====
  spiritual_1: {
    hi: {
      q: 'कितनी बार आप भीतर की शांति और साफ़ मक़सद महसूस करते हैं?',
      o: ['बहुत कम — दिशाहीन लगता है', 'कभी-कभार — मक़सद अस्पष्ट', 'कभी-कभी — कुछ पल साफ़', 'अक्सर — दिशा साफ़ है', 'लगभग रोज़ — गहरा मक़सद और शांति'],
    },
    mr: {
      q: 'किती वेळा तुम्हाला आंतरिक शांती आणि स्पष्ट उद्देश जाणवतो?',
      o: ['फारच क्वचित — दिशाहीन वाटतं', 'अधूनमधून — उद्देश अस्पष्ट', 'कधीकधी — काही क्षण स्पष्ट', 'बऱ्याचदा — दिशा स्पष्ट', 'जवळजवळ रोज — खोल उद्देश आणि शांती'],
    },
  },
  spiritual_2: {
    hi: {
      q: 'क्या आपको ज़िंदगी में एक मक़सद महसूस होता है?',
      o: ['बिल्कुल नहीं', 'थोड़ा-सा', 'कुछ हद तक', 'ज़ोरदार', 'गहरा और साफ़ मक़सद'],
    },
    mr: {
      q: 'तुम्हाला आयुष्यात एक उद्देश जाणवतो का?',
      o: ['अजिबात नाही', 'थोडासा', 'काहीसा', 'खूप', 'खोल आणि स्पष्ट उद्देश'],
    },
  },

  // ===== SELF-CARE =====
  self_care_1: {
    hi: {
      q: 'सेल्फ़-केयर (ग्रूमिंग, स्किनकेयर, रिकवरी, हेल्थ चेकअप) में कितना निवेश करते हैं और ख़ुद को लेकर कितना आत्मविश्वास है?',
      o: ['कम ही निवेश; बहुत कम आत्मविश्वास', 'कभी-कभार; ख़ुद से असंतुष्ट', 'कभी-कभी; ख़ुद के बारे में सामान्य', 'नियमित; ज़्यादातर संतुष्ट और आत्मविश्वासी', 'लगातार प्राथमिकता; गहरा आत्मविश्वास'],
    },
    mr: {
      q: 'सेल्फ-केअर (ग्रूमिंग, स्किनकेअर, रिकव्हरी, हेल्थ चेकअप) मध्ये तुम्ही किती गुंतवणूक करता आणि स्वतःबद्दल किती आत्मविश्वास आहे?',
      o: ['क्वचित गुंतवणूक; खूप कमी आत्मविश्वास', 'अधूनमधून; स्वतःबद्दल असमाधानी', 'कधीकधी; स्वतःबद्दल सामान्य', 'नियमित; बहुतांश समाधानी आणि आत्मविश्वासी', 'सतत प्राधान्यता; खोल आत्मविश्वास'],
    },
  },
  self_care_2: {
    hi: {
      q: 'स्किनकेयर, ख़ुद का ख़याल और वेलनेस (हेल्थ चेकअप, ग्रूमिंग आदि) में कितनी बार निवेश करते हैं?',
      o: ['लगभग कभी नहीं', 'कम ही', 'कभी-कभार', 'नियमित रूप से', 'लगातार — यह मेरी प्राथमिकता है'],
    },
    mr: {
      q: 'स्किनकेअर, स्वतःची काळजी आणि वेलनेसमध्ये (हेल्थ चेकअप, ग्रूमिंग वगैरे) तुम्ही किती गुंतवणूक करता?',
      o: ['जवळजवळ कधीच नाही', 'क्वचितच', 'अधूनमधून', 'नियमित', 'सतत — ती माझी प्राधान्यता आहे'],
    },
  },
};

/**
 * Translate a backend question object for the selected language.
 * Falls back to the original English question if no translation exists.
 */
export function translateQuestion(question, lang) {
  if (!question || lang === 'en') return question;
  const entry = QUESTION_I18N[question.id]?.[lang];
  if (!entry) return question;
  return {
    ...question,
    question: entry.q,
    options: question.options.map((opt) => {
      const tText = entry.o?.[opt.value - 1];
      return tText ? { ...opt, text: tText } : opt;
    }),
  };
}

export default QUESTION_I18N;
